import { useState, useCallback, useEffect, useRef } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useKubernetesObjects, kubernetesApiRef } from '@backstage/plugin-kubernetes-react';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  makeStyles,
  Tab,
  Tabs,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
  Tooltip,
  Drawer,
  IconButton,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SubjectIcon from '@material-ui/icons/Subject';

const ENV_ORDER = ['dev', 'qa', 'stg', 'prd'];
const ENV_LABELS: Record<string, string> = { dev: 'Dev', qa: 'QA', stg: 'Staging', prd: 'Production' };

// ─── ArgoCD ───────────────────────────────────────────────────────────────────
// One ArgoCD instance per environment. Applications are named {appKey}-{serviceKey}
// and live under the "argocd" project.
//
// Live sync/health status comes from ArgoCD's native status badge endpoint
// (/api/badge?name=...), but the browser retrieves it through the authenticated
// Backstage backend proxy. ArgoCD can then allowlist only the backend for badge
// access while the UI remains transparent to signed-in users.
// https://argo-cd.readthedocs.io/en/stable/user-guide/status-badge/

const ARGOCD_BASE: Record<string, string> = {
  dev: 'https://argocd.dev.dtcloudhosting.com',
  qa: 'https://argocd.qa.dtcloudhosting.com',
  stg: 'https://argocd.stg.dtcloudhosting.com',
  prd: 'https://argocd.prd.dtcloudhosting.com',
};

function argocdAppName(appKey: string, serviceKey: string): string {
  return `${appKey}-${serviceKey}`;
}

function argocdAppUrl(env: string, appKey: string, serviceKey: string): string | undefined {
  const base = ARGOCD_BASE[env];
  if (!base || !appKey || !serviceKey) return undefined;
  const app = encodeURIComponent(argocdAppName(appKey, serviceKey));
  return `${base}/applications/argocd/${app}?view=tree&resource=&orphaned=false`;
}

// How often the status badge is re-fetched. ArgoCD reflects a health change in
// its own status within seconds, so this interval is what actually determines
// how quickly the portal shows it.
const BADGE_REFRESH_MS = 60_000;
const KUBERNETES_REFRESH_MS = 60_000;

// Path relative to the argocd plugin's base URL — resolved against the real
// backend origin via discoveryApi at fetch time. It must NOT be a bare
// '/api/argocd/badge', because now that frontend and backend are separate
// services the browser would resolve that against the frontend's own origin
// and never reach the backend.
function argocdBadgeApiPath(env: string, appKey: string, serviceKey: string, cacheBust?: number): string | undefined {
  if (!ARGOCD_BASE[env] || !appKey || !serviceKey) return undefined;
  const params = new URLSearchParams({
    env,
    appKey,
    serviceKey,
  });
  if (cacheBust !== undefined) {
    params.set('t', String(cacheBust));
  }
  return `/badge?${params.toString()}`;
}

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(2, 0) },
  tabs: { borderBottom: `1px solid ${theme.palette.divider}`, marginBottom: theme.spacing(2), minHeight: 40 },
  tab: { minHeight: 40, minWidth: 80, textTransform: 'none', fontWeight: 500 },
  // No alignItems here — the default "stretch" is what keeps both cards the
  // same height regardless of which one has more content.
  summaryGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 260px) minmax(0, 1fr)', gap: theme.spacing(1.5), marginBottom: theme.spacing(2), maxWidth: 900 },
  card: { border: `1px solid ${theme.palette.divider}`, borderRadius: 8, padding: theme.spacing(1.5, 2), background: theme.palette.background.paper },
  cardTitle: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: theme.palette.text.secondary, marginBottom: theme.spacing(1), display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) },
  metricNum: { fontSize: 26, fontWeight: 500, lineHeight: 1 },
  metricLbl: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  metricSub: { fontSize: 10, color: theme.palette.text.hint, marginTop: 4 },
  argoHead: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: theme.palette.text.secondary, marginBottom: theme.spacing(1), display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) },
  argoLink: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, letterSpacing: 0, textTransform: 'none', color: theme.palette.primary.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
  argoBadgeLink: { display: 'block', textDecoration: 'none', width: '100%', '&:hover': { opacity: 0.85 } },
  // The badge SVG is two stacked rows when showAppName is on, so a small fixed
  // height makes each row unreadable. Scale it up instead — it is vector, so it
  // stays sharp. Capped so the card does not grow very tall on wide screens,
  // since height scales with width.
  argoBadgeImg: { display: 'block', width: '100%', maxWidth: 520, height: 'auto' },
  podTable: { width: '100%', marginTop: theme.spacing(1) },
  podTh: { '& th': { fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.palette.text.secondary, padding: '6px 8px', borderBottom: `1px solid ${theme.palette.divider}` } },
  podRow: { cursor: 'pointer', '&:hover': { background: theme.palette.action.hover }, '&:last-child td': { borderBottom: 'none' }, '& td': { padding: '7px 8px', fontSize: 12, borderBottom: `1px solid ${theme.palette.divider}` } },
  podName: { fontWeight: 500, fontFamily: 'monospace', fontSize: 11 },
  podNs: { fontSize: 10, color: theme.palette.text.hint, marginTop: 1 },
  badgeOk: { background: '#e6f4ea', color: '#1e7e34', fontSize: 11, height: 20 },
  badgeWarn: { background: '#fff3cd', color: '#856404', fontSize: 11, height: 20 },
  badgeErr: { background: '#fce8e6', color: '#c5221f', fontSize: 11, height: 20 },
  notConnected: { padding: theme.spacing(3), textAlign: 'center', border: `1px solid ${theme.palette.divider}`, borderRadius: 8, color: theme.palette.text.secondary, fontSize: 13, marginTop: theme.spacing(1) },
  sectionTitle: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: theme.palette.text.secondary, marginBottom: theme.spacing(1), marginTop: theme.spacing(2) },

  // Drawer
  drawer: { width: 520 },
  drawerHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: theme.spacing(2, 2, 1) },
  drawerTitle: { fontSize: 15, fontWeight: 600, color: theme.palette.text.primary, fontFamily: 'monospace', wordBreak: 'break-all' },
  drawerSubtitle: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  drawerSection: { padding: theme.spacing(1.5, 2) },
  drawerSectionTitle: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: theme.palette.text.secondary, marginBottom: theme.spacing(1) },
  drawerKv: { display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `0.5px solid ${theme.palette.divider}`, fontSize: 12 },
  drawerKvLast: { borderBottom: 'none' },
  drawerKey: { color: theme.palette.text.secondary },
  drawerVal: { color: theme.palette.text.primary, fontWeight: 500, fontFamily: 'monospace', fontSize: 11, textAlign: 'right', maxWidth: 280, wordBreak: 'break-all' },
  imageChip: { fontFamily: 'monospace', fontSize: 10, height: 20, maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } },
  logsBtn: { marginTop: theme.spacing(1.5), textTransform: 'none', fontWeight: 500 },
  logsDialog: { '& .MuiDialog-paper': { width: '80vw', maxWidth: 1000, height: '80vh', display: 'flex', flexDirection: 'column' } },
  logsDialogTitle: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: theme.spacing(1.5, 2), borderBottom: `1px solid ${theme.palette.divider}` },
  logsDialogTitleText: { fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: theme.palette.text.primary },
  logsDialogContent: { flex: 1, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  logBox: { flex: 1, background: '#1e1e1e', color: '#d4d4d4', fontFamily: 'monospace', fontSize: 12, padding: theme.spacing(2), overflow: 'auto', whiteSpace: 'pre', lineHeight: 1.7 },
  logLoading: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing(1.5), flex: 1, fontSize: 13, color: '#aaa', background: '#1e1e1e' },
  liveDot: { width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block', marginRight: 4, animation: '$pulse 1.5s infinite' },
  '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.3 }, '100%': { opacity: 1 } },
}));

function getPodChipLabel(isOk: boolean, isWarn: boolean): string {
  if (isOk) return 'OK';
  if (isWarn) return 'Warning';
  return 'Error';
}

function getPodChipClass(isOk: boolean, isWarn: boolean, classes: ReturnType<typeof useStyles>): string {
  if (isOk) return classes.badgeOk;
  if (isWarn) return classes.badgeWarn;
  return classes.badgeErr;
}

function PodStatusChip({ isOk, isWarn, classes }: { isOk: boolean; isWarn: boolean; classes: ReturnType<typeof useStyles> }) {
  return <Chip label={getPodChipLabel(isOk, isWarn)} size="small" className={getPodChipClass(isOk, isWarn, classes)} />;
}

interface SelectedPod { pod: any; clusterName: string; namespace: string; }

interface LogsModalProps { open: boolean; onClose: () => void; pod: any; clusterName: string; namespace: string; }

const LIVE_INTERVAL_MS = 5000;

function LogsModal({ open, onClose, pod, clusterName, namespace }: LogsModalProps) {
  const classes = useStyles();
  const kubernetesApi = useApi(kubernetesApiRef);
  const [logs, setLogs]         = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);
  const [live, setLive]         = useState(true);
  const [userScrolled, setUserScrolled] = useState(false);
  const logBoxRef  = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const podName       = pod?.metadata?.name ?? '';
  const containerName = pod?.spec?.containers?.[0]?.name ?? '';

  const fetchLogs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const path = `/api/v1/namespaces/${namespace}/pods/${podName}/log?container=${containerName}&tailLines=1000&timestamps=true`;
      const resp = await (kubernetesApi as any).proxy({ clusterName, path });
      const text = typeof resp === 'string' ? resp : await resp.text?.() ?? JSON.stringify(resp);
      setLogs(text);
    } catch (e: any) {
      setLogs(`Error fetching logs:\n${e?.message ?? String(e)}`);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [podName, containerName, namespace, clusterName, kubernetesApi]);

  // Auto-scroll to bottom unless user scrolled up
  useEffect(() => {
    if (!userScrolled && logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [logs, userScrolled]);

  // Detect user scrolling up
  const handleScroll = () => {
    const el = logBoxRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setUserScrolled(!atBottom);
  };

  // Initial fetch + live interval
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setUserScrolled(false);
    fetchLogs();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [open, fetchLogs]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (open && live) {
      intervalRef.current = setInterval(() => fetchLogs(true), LIVE_INTERVAL_MS);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [open, live, fetchLogs]);

  const scrollToBottom = () => {
    if (logBoxRef.current) logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    setUserScrolled(false);
  };

  return (
    <Dialog open={open} onClose={onClose} className={classes.logsDialog} maxWidth={false}>
      <DialogTitle disableTypography className={classes.logsDialogTitle}>
        <div>
          <span className={classes.logsDialogTitleText}>{podName}</span>
          <span style={{ fontSize: 11, color: '#888', marginLeft: 10 }}>container: {containerName} · last 1000 lines</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {live && !loading && <span style={{ fontSize: 11, color: '#1D9E75', display: 'flex', alignItems: 'center' }}><span className={classes.liveDot} />Live</span>}
          {loading && <CircularProgress size={14} style={{ color: '#aaa' }} />}
          <Button
            size="small"
            variant={live ? 'contained' : 'outlined'}
            color={live ? 'primary' : 'default'}
            style={{ textTransform: 'none', fontSize: 11, minWidth: 60 }}
            onClick={() => setLive(l => !l)}
          >
            {live ? 'Pause' : 'Live'}
          </Button>
          <Button size="small" variant="outlined" style={{ textTransform: 'none', fontSize: 11 }} onClick={() => fetchLogs()}>
            Refresh
          </Button>
          {userScrolled && (
            <Button size="small" variant="outlined" style={{ textTransform: 'none', fontSize: 11 }} onClick={scrollToBottom}>
              ↓ Bottom
            </Button>
          )}
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={classes.logsDialogContent}>
        {loading && !logs ? (
          <div className={classes.logLoading}>
            <CircularProgress size={16} style={{ color: '#aaa' }} />
            <span>Fetching logs…</span>
          </div>
        ) : (
          <div
            ref={logBoxRef}
            className={classes.logBox}
            onScroll={handleScroll}
          >
            {logs ?? 'No logs available.'}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PodDrawer({ selected, onClose, kubernetesObjects }: { selected: SelectedPod | null; onClose: () => void; kubernetesObjects: any; }) {
  const classes = useStyles();
  const [logsOpen, setLogsOpen] = useState(false);

  if (!selected) return null;
  const { pod, clusterName, namespace } = selected;
  const podName: string = pod.metadata?.name ?? '';
  const containers: any[] = pod.status?.containerStatuses ?? [];
  const specContainers: any[] = pod.spec?.containers ?? [];
  const phase: string = pod.status?.phase ?? '—';
  const podIP: string = pod.status?.podIP ?? '—';
  const nodeName: string = pod.spec?.nodeName ?? '—';
  const startTime: string = pod.status?.startTime ? new Date(pod.status.startTime).toLocaleString() : '—';
  const ready = containers.filter((c: any) => c.ready).length;
  const restarts = containers.reduce((s: number, c: any) => s + (c.restartCount ?? 0), 0);
  const isOk = phase === 'Running' && ready === containers.length;

  const clusterData = kubernetesObjects?.items?.find((i: any) => i.cluster.name === clusterName);
  const deployments = (clusterData?.resources ?? []).filter((r: any) => r.type === 'deployments').flatMap((r: any) => r.resources ?? []);
  const deployment = deployments.find((d: any) => {
    const selector = d.spec?.selector?.matchLabels ?? {};
    const podLabels = pod.metadata?.labels ?? {};
    return Object.entries(selector).every(([k, v]) => podLabels[k] === v);
  });

  return (
    <>
      <Drawer anchor="right" open={!!selected} onClose={onClose} classes={{ paper: classes.drawer }}>
        <div className={classes.drawerHeader}>
          <div>
            <div className={classes.drawerTitle}>{podName}</div>
            <div className={classes.drawerSubtitle}>Pod · {namespace} · {clusterName}</div>
          </div>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </div>
        <Divider />

        <div className={classes.drawerSection}>
          <div className={classes.drawerSectionTitle}>Status</div>
          <div className={classes.drawerKv}><span className={classes.drawerKey}>Phase</span><span className={classes.drawerVal}>{phase}</span></div>
          <div className={classes.drawerKv}><span className={classes.drawerKey}>Ready</span><span className={classes.drawerVal}>{ready}/{containers.length}</span></div>
          <div className={classes.drawerKv}><span className={classes.drawerKey}>Health</span><span className={classes.drawerVal}><Chip label={isOk ? 'OK' : 'Warning'} size="small" className={isOk ? classes.badgeOk : classes.badgeWarn} /></span></div>
          <div className={classes.drawerKv}><span className={classes.drawerKey}>Restarts</span><span className={classes.drawerVal} style={restarts > 0 ? { color: '#c5221f' } : {}}>{restarts}</span></div>
          <div className={classes.drawerKv}><span className={classes.drawerKey}>Started</span><span className={classes.drawerVal}>{startTime}</span></div>
          <div className={classes.drawerKv}><span className={classes.drawerKey}>Pod IP</span><span className={classes.drawerVal}>{podIP}</span></div>
          <div className={`${classes.drawerKv} ${classes.drawerKvLast}`}><span className={classes.drawerKey}>Node</span><span className={classes.drawerVal}>{nodeName}</span></div>
        </div>
        <Divider />

        <div className={classes.drawerSection}>
          <div className={classes.drawerSectionTitle}>Containers</div>
          {specContainers.map((c: any, i: number) => {
            const status = containers.find((s: any) => s.name === c.name);
            return (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{c.name}</div>
                <div className={classes.drawerKv}><span className={classes.drawerKey}>Image</span><Tooltip title={c.image ?? ''}><Chip label={c.image} size="small" className={classes.imageChip} /></Tooltip></div>
                <div className={classes.drawerKv}><span className={classes.drawerKey}>Ready</span><span className={classes.drawerVal}>{status?.ready ? '✓ Yes' : '✗ No'}</span></div>
                <div className={classes.drawerKv}><span className={classes.drawerKey}>Restarts</span><span className={classes.drawerVal}>{status?.restartCount ?? 0}</span></div>
                <div className={classes.drawerKv}><span className={classes.drawerKey}>CPU request</span><span className={classes.drawerVal}>{c.resources?.requests?.cpu ?? '—'}</span></div>
                <div className={classes.drawerKv}><span className={classes.drawerKey}>CPU limit</span><span className={classes.drawerVal}>{c.resources?.limits?.cpu ?? '—'}</span></div>
                <div className={classes.drawerKv}><span className={classes.drawerKey}>Memory request</span><span className={classes.drawerVal}>{c.resources?.requests?.memory ?? '—'}</span></div>
                <div className={`${classes.drawerKv} ${classes.drawerKvLast}`}><span className={classes.drawerKey}>Memory limit</span><span className={classes.drawerVal}>{c.resources?.limits?.memory ?? '—'}</span></div>
              </div>
            );
          })}
        </div>

        {deployment && (
          <>
            <Divider />
            <div className={classes.drawerSection}>
              <div className={classes.drawerSectionTitle}>Deployment</div>
              <div className={classes.drawerKv}><span className={classes.drawerKey}>Name</span><span className={classes.drawerVal}>{deployment.metadata?.name}</span></div>
              <div className={classes.drawerKv}><span className={classes.drawerKey}>Replicas</span><span className={classes.drawerVal}>{deployment.status?.readyReplicas ?? 0} / {deployment.spec?.replicas ?? 0} ready</span></div>
              <div className={classes.drawerKv}><span className={classes.drawerKey}>Strategy</span><span className={classes.drawerVal}>{deployment.spec?.strategy?.type ?? '—'}</span></div>
              <div className={`${classes.drawerKv} ${classes.drawerKvLast}`}><span className={classes.drawerKey}>Latest image</span><Tooltip title={deployment.spec?.template?.spec?.containers?.[0]?.image ?? ''}><Chip label={deployment.spec?.template?.spec?.containers?.[0]?.image ?? '—'} size="small" className={classes.imageChip} /></Tooltip></div>
            </div>
          </>
        )}

        <Divider />
        <div className={classes.drawerSection}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SubjectIcon />}
            className={classes.logsBtn}
            onClick={() => setLogsOpen(true)}
            fullWidth
          >
            View Logs
          </Button>
        </div>
      </Drawer>

      <LogsModal
        open={logsOpen}
        onClose={() => setLogsOpen(false)}
        pod={pod}
        clusterName={clusterName}
        namespace={namespace}
      />
    </>
  );
}

export function EntityEnvironmentsContent() {
  const classes = useStyles();
  const { entity } = useEntity();
  const { kubernetesObjects, loading } = useKubernetesObjects(entity, KUBERNETES_REFRESH_MS);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  // useKubernetesObjects flips `loading` true on every background poll, not
  // just the first fetch — without this, the pod table and summary cards
  // would flash a loading state every refresh interval. Only treat loading
  // as UI-blocking until the first successful load has landed; after that,
  // refreshes update data silently in place.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!loading && kubernetesObjects !== undefined) setHasLoadedOnce(true);
  }, [loading, kubernetesObjects]);
  const isInitialLoading = loading && !hasLoadedOnce;

  const serviceKey = entity.metadata.name;
  const appKey     = entity.metadata.labels?.['app-key'] ?? '';
  const rawEnvs    = entity.metadata.annotations?.['dt/env-folders'] ?? 'dev';
  const envs       = ENV_ORDER.filter(e => rawEnvs.split(',').map((x: string) => x.trim()).includes(e));

  const [activeEnv, setActiveEnv] = useState(envs[0] ?? 'dev');
  const [selectedPod, setSelectedPod] = useState<SelectedPod | null>(null);

  // The badge is re-fetched on an interval through the Backstage backend proxy,
  // keeping it roughly as fresh as the pod data below it.
  const [badgeTick, setBadgeTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setBadgeTick(Date.now()), BADGE_REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const getClusterData = (env: string) =>
    kubernetesObjects?.items.find((item: any) => item.cluster.name === `dt-shared-${env}`)
    ?? (env === 'dev' ? kubernetesObjects?.items.find((item: any) => item.cluster.name === 'docker-desktop') : undefined);

  const getClusterName = (env: string) => {
    const data = kubernetesObjects?.items.find((item: any) => item.cluster.name === `dt-shared-${env}`);
    if (data) return `dt-shared-${env}`;
    if (env === 'dev') return 'docker-desktop';
    return `dt-shared-${env}`;
  };

  const getPods = (env: string) =>
    (getClusterData(env)?.resources ?? [])
      .filter((r: any) => r.type === 'pods')
      .flatMap((r: any) => r.resources ?? []);

  // A cluster counts as connected when the Kubernetes backend actually returned
  // data for it. Deriving this at runtime (instead of a hardcoded env list)
  // means a new cluster lights up as soon as it is added to app-config — no
  // frontend change or rebuild needed. While the request is still in flight we
  // assume connected so the UI shows a loading state rather than flashing
  // "not connected".
  const isClusterConnected = (env: string) => isInitialLoading || getClusterData(env) !== undefined;

  function SummaryCards({ env }: { env: string }) {
    const isConnected = isClusterConnected(env);
    const pods    = isConnected ? getPods(env) : [];
    const running = pods.filter((p: any) => p.status?.phase === 'Running').length;
    const errors  = pods.filter((p: any) => p.status?.phase !== 'Running').length;
    return (
      <div className={classes.summaryGrid}>
        <div className={classes.card}>
          <div className={classes.cardTitle}>Kubernetes</div>
          {isConnected ? (
            <>
              <div className={classes.metricNum} style={{ color: errors > 0 ? '#c5221f' : '#1D9E75' }}>{isInitialLoading ? '…' : running}</div>
              <div className={classes.metricLbl}>{errors > 0 ? `${errors} pod(s) not running` : 'pods running'}</div>
              <div className={classes.metricSub}>namespace: {appKey} · dt-shared-{env}</div>
            </>
          ) : (
            <>
              <div className={classes.metricNum} style={{ color: '#aaa' }}>—</div>
              <div className={classes.metricLbl}>Cluster not connected</div>
            </>
          )}
        </div>
        <ArgoCard env={env} />
      </div>
    );
  }

  function ArgoCard({ env }: { env: string }) {
    const url      = argocdAppUrl(env, appKey, serviceKey);
    const badgeUrl = argocdBadgeApiPath(env, appKey, serviceKey, badgeTick);
    // The badge is served through the Backstage backend. If the backend cannot
    // retrieve it from ArgoCD, fall back to text rather than showing a broken
    // image.
    const [badgeFailed, setBadgeFailed] = useState(false);
    const [shownSrc, setShownSrc] = useState<string | undefined>();
    const shownSrcRef = useRef<string | undefined>();

    useEffect(() => {
      if (!badgeUrl) return undefined;
      let cancelled = false;

      const loadBadge = async () => {
        try {
          // A plain relative fetch bypasses fetchApi's own URL resolution, so
          // it also never gets the identity token attached — resolve the base
          // URL and attach it explicitly, or the backend answers 401.
          const baseUrl = await discoveryApi.getBaseUrl('argocd');
          const { token } = await identityApi.getCredentials();
          const response = await fetchApi.fetch(`${baseUrl}${badgeUrl}`, {
            headers: {
              Accept: 'image/svg+xml',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          if (!response.ok) {
            throw new Error(`ArgoCD badge request failed: ${response.status}`);
          }
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          if (shownSrcRef.current) {
            URL.revokeObjectURL(shownSrcRef.current);
          }
          shownSrcRef.current = objectUrl;
          setShownSrc(objectUrl);
          setBadgeFailed(false);
        } catch {
          if (!cancelled) {
            setBadgeFailed(true);
          }
        }
      };

      loadBadge();
      return () => {
        cancelled = true;
      };
    }, [badgeUrl]);

    useEffect(() => {
      return () => {
        if (shownSrcRef.current) {
          URL.revokeObjectURL(shownSrcRef.current);
        }
      };
    }, []);

    if (!url || !badgeUrl) {
      return (
        <div className={classes.card}>
          <div className={classes.argoHead}><span>Argo-CD</span></div>
          <div className={classes.metricNum} style={{ color: '#aaa' }}>—</div>
          <div className={classes.metricLbl}>
            {ARGOCD_BASE[env] ? 'Application key not set on this entity' : 'No ArgoCD instance for this environment'}
          </div>
        </div>
      );
    }

    return (
      <div className={classes.card}>
        <div className={classes.argoHead}>
          <span>Argo-CD</span>
          <a href={url} target="_blank" rel="noopener noreferrer" className={classes.argoLink}>
            Open <OpenInNewIcon style={{ fontSize: 12 }} />
          </a>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.argoBadgeLink}
          title={`${argocdAppName(appKey, serviceKey)} — open in ArgoCD`}
        >
          {badgeFailed ? (
            <span className={classes.metricLbl}>Status unavailable</span>
          ) : (
            <img
              src={shownSrc}
              alt={`ArgoCD sync and health status for ${argocdAppName(appKey, serviceKey)}`}
              className={classes.argoBadgeImg}
              // Don't leak the catalog URL being viewed into ArgoCD's access logs.
              referrerPolicy="no-referrer"
            />
          )}
        </a>
      </div>
    );
  }

  function PodTable({ env }: { env: string }) {
    const isConnected = isClusterConnected(env);
    if (!isConnected) return (
      <div className={classes.notConnected}>
        <div style={{ marginBottom: 4 }}>Cluster <strong>dt-shared-{env}</strong> is not connected yet.</div>
        <div style={{ fontSize: 11, color: '#aaa' }}>This environment will appear automatically once the cluster is added to the Backstage configuration.</div>
      </div>
    );
    if (isInitialLoading) return <LinearProgress style={{ marginTop: 8 }} />;
    const pods = getPods(env);
    const clusterName = getClusterName(env);

    if (!pods.length) return (
      <div className={classes.notConnected}>
        No pods found in <strong>dt-shared-{env}</strong> matching <code>app={serviceKey}</code> in namespace <code>{appKey}</code>.
      </div>
    );

    return (
      <Table className={classes.podTable} size="small">
        <TableHead className={classes.podTh}>
          <TableRow>
            <TableCell>Pod</TableCell><TableCell>Phase</TableCell><TableCell>Status</TableCell>
            <TableCell>Ready</TableCell><TableCell>Restarts</TableCell>
            <TableCell>Mem Request</TableCell><TableCell>CPU Request</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pods.map((pod: any) => {
            const podName: string = pod.metadata?.name ?? '';
            const phase: string   = pod.status?.phase ?? '—';
            const containers: any[] = pod.status?.containerStatuses ?? [];
            const ready    = containers.filter((c: any) => c.ready).length;
            const restarts = containers.reduce((s: number, c: any) => s + (c.restartCount ?? 0), 0);
            const isOk     = phase === 'Running' && ready === containers.length;
            const isWarn   = phase === 'Pending' || restarts > 0;
            const fc       = pod.spec?.containers?.[0];
            return (
              <TableRow
                key={podName}
                className={classes.podRow}
                onClick={() => setSelectedPod({ pod, clusterName, namespace: appKey })}
              >
                <TableCell>
                  <Tooltip title="Click to view details & logs">
                    <div>
                      <div className={classes.podName}>{podName.length > 40 ? `${podName.slice(0, 37)}…` : podName}</div>
                      <div className={classes.podNs}>{appKey}</div>
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell>{phase}</TableCell>
                <TableCell>
                  <PodStatusChip isOk={isOk} isWarn={isWarn} classes={classes} />
                </TableCell>
                <TableCell>{ready}/{containers.length}</TableCell>
                <TableCell style={restarts > 0 ? { color: '#c5221f', fontWeight: 600 } : {}}>{restarts}</TableCell>
                <TableCell>
                  <span style={{ fontSize: 12, color: '#555' }}>{fc?.resources?.requests?.memory ?? '—'}</span>
                </TableCell>
                <TableCell>
                  <span style={{ fontSize: 12, color: '#555' }}>{fc?.resources?.requests?.cpu ?? '—'}</span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className={classes.root}>
      <Tabs className={classes.tabs} value={activeEnv} onChange={(_, v) => setActiveEnv(v)} indicatorColor="primary" textColor="primary">
        {envs.map(env => <Tab key={env} value={env} label={ENV_LABELS[env] ?? env} className={classes.tab} />)}
      </Tabs>

      {envs.map(env => (
        <div key={env} hidden={activeEnv !== env}>
          <SummaryCards env={env} />
          <div className={classes.sectionTitle}>Pods · dt-shared-{env}</div>
          <div className={classes.card}><PodTable env={env} /></div>
        </div>
      ))}

      <PodDrawer
        selected={selectedPod}
        onClose={() => setSelectedPod(null)}
        kubernetesObjects={kubernetesObjects}
      />
    </div>
  );
}
